import { useRouter } from 'next/router';
import React, { useEffect, useState, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import Select, {
  components,
  MenuPlacement,
  MultiValueProps,
  OptionProps,
  type DropdownIndicatorProps,
} from 'react-select';

interface CheckboxOptionProps {
  isSelectAll?: boolean;
  withSelectAll?: boolean;
  countPageDisplayed?: Record<string, number> | [];
  countPageDisplayedMax?: number;
  modalType?: string;
  /* eslint-disable-next-line */
  value?: any;
  label?: string;
  price?: number;
}

const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <div
        className={`transition-transform duration-200 ${props.selectProps.menuIsOpen ? 'rotate-180' : ''}`}
      >
        <svg
          width="11"
          height="8"
          viewBox="0 0 10 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.825 0.158325L5 3.97499L1.175 0.158325L0 1.33333L5 6.33332L10 1.33333L8.825 0.158325Z"
            fill="#4F4C4D"
          />
        </svg>
      </div>
    </components.DropdownIndicator>
  );
};

const CheckboxOption = ({
  isSelectAll,
  withSelectAll,
  ...props
  /* eslint-disable-next-line */
}: CheckboxOptionProps & OptionProps<any, boolean, any>) => {
  const isPageNCPReview = window.location.pathname?.includes(
    'seo-static-page/listing-model-car/review'
  );
  const isSelectAllDisabled = isPageNCPReview
    ? props.options?.filter((item) => item.isDisabled)?.length ==
      props.options?.filter((item) => item.id)?.length
    : false;

  return (
    <div className="flex items-center p-3 border-b-2" {...props.innerProps}>
      <input
        type="checkbox"
        checked={
          withSelectAll
            ? isSelectAll && props.value === 'select-all'
              ? isSelectAll
              : props.isSelected
            : props.isSelected
        }
        onChange={() => {}}
        className={`border-2 ${
          (withSelectAll && isSelectAllDisabled) || props.isDisabled
            ? '!border-gray-300 checked:!bg-gray-300 checked:!border-gray-300'
            : 'checked:bg-red-600 checked:border-red-600 !border-supportiveRed'
        }`}
      />
      <div className="w-full flex justify-between items-center font-semibold">
        <p
          className={`ml-3 ${
            ((withSelectAll && isSelectAllDisabled) || props.isDisabled) && '!text-gray-300'
          }`}
        >
          {props.label}
        </p>
        <p
          className={`ml-3 ${
            ((withSelectAll && isSelectAllDisabled) || props.isDisabled) && '!text-gray-300'
          }`}
        >
          {props?.options?.find((item) => item.label === props.label) &&
            props?.options?.find((item) => item.label === props.label)?.price &&
            `Rp${props?.options?.find((item) => item.label === props.label)?.price}`}
        </p>
      </div>
    </div>
  );
};

const customStyles = {
  option: (provided, state) => {
    return {
      ...provided,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px',
      cursor: 'pointer',
      backgroundColor: state.isSelected ? '#d3d3d3' : 'white',
    };
  },
};

/* eslint-disable-next-line */
interface Option {
  /* eslint-disable-next-line */
  code?: any;
  /* eslint-disable-next-line */
  key?: any;
  label: string;
  value: string;
  isDisabled?: boolean;
}

interface CustomMultiSelectProps {
  name?: string;
  /* eslint-disable-next-line */
  control?: any;
  options: Option[];
  isMulti: boolean;
  placeholder?: string;
  className?: string;
  required?: boolean;
  labelName?: string;
  showMessageError?: boolean;
  error?: string;
  selectedDropDown?: (selectedOption: Option[]) => void;
  containerClassName?: string;
  checkedSelect?: boolean;
  isDisabled?: boolean;
  value?: Option[];
  maxToShown?: number;
  isSeo?: boolean;
  useController?: boolean;
  onChangeData?: (selectedOption: Option[]) => void;
  menuPlacement?: MenuPlacement;
  maxMenuHeight?: number;
  menuIsOpen?: boolean;
  onMenuOpen?: () => void;
  isLoading?: boolean;
  withSelectAll?: boolean;
  countPageDisplayed?: Record<string, number> | [];
  countPageDisplayedMax?: number;
  modalType?: string;
}

const CustomMultiSelect = ({
  name,
  control,
  options,
  isMulti,
  placeholder,
  className,
  required,
  labelName,
  showMessageError = true,
  error,
  selectedDropDown = () => {},
  containerClassName,
  checkedSelect = false,
  isDisabled,
  value,
  isSeo = false,
  useController = false,
  onChangeData,
  menuPlacement = 'auto',
  maxMenuHeight = 360,
  menuIsOpen,
  onMenuOpen = () => {},
  isLoading = false,
  withSelectAll = false,
  countPageDisplayed = [],
  countPageDisplayedMax = 3,
  modalType = '',
}: CustomMultiSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectOptions, setSelectOptions] = useState(options);
  const router = useRouter();
  const isPageNCPTabReview = router?.pathname?.includes(
    '/seo-static-page/listing-model-car/review'
  );

  const handleInputChange = (newValue) => {
    // const filteredValue = preventEmoji(newValue);
    setInputValue(newValue);
  };

  const handleMenuOpen = () => {
    if (!isPageNCPTabReview) setIsSelectAll(value?.length == options?.length ? true : false);
    onMenuOpen();
  };

  // Conditional logic based on isPageNCPTabReview
  useEffect(() => {
    if (isMulti && withSelectAll) {
      const baseOptions: Option[] =
        (options ?? []).length > 0
          ? [
              {
                label: 'Pilih Semua',
                value: 'select-all',
                isDisabled: isPageNCPTabReview ? options?.every((item) => item?.isDisabled) : false,
              },
              ...options,
            ]
          : [];

      setSelectOptions(baseOptions);
    }
  }, [options, value]);

  // This conditonal for page NCP - review tab
  const initialSelectAll = useMemo(() => {
    if (isMulti && withSelectAll) {
      const validLabels = new Set(options.map((item) => item.label.toLowerCase()));

      const filteredCountPageDisplayed = Object.keys(countPageDisplayed)
        .filter((key) => validLabels.has(key))
        .reduce((obj, key) => {
          obj[key] = countPageDisplayed[key];
          return obj;
        }, {});

      const allItemsHaveThreeOrMore = options?.every((item) => {
        const count = filteredCountPageDisplayed[item.label?.toLowerCase()];
        return count >= countPageDisplayedMax;
      });

      return allItemsHaveThreeOrMore;
    }
    return false;
  }, [options, countPageDisplayed, countPageDisplayedMax, isMulti, withSelectAll]);

  useEffect(() => {
    if ((modalType === 'add' && !initialSelectAll) || modalType === 'edit') {
      setIsSelectAll(initialSelectAll);
    }
  }, [initialSelectAll, modalType, value]);

  useEffect(() => {
    if (isMulti && withSelectAll) {
      if (value?.length == options?.length) {
        setIsSelectAll(true);
      }
    }
  }, [value, options, isMulti, withSelectAll, isPageNCPTabReview]);

  const handleSelectChange = (selectedOption) => {
    if (withSelectAll) {
      if (!isPageNCPTabReview) {
        // For other pages
        const _isSelectAll = selectedOption?.find((item) => item?.value === 'select-all');
        if (_isSelectAll) {
          setIsSelectAll((prevIsSelectAll) => {
            const newIsSelectAll = !prevIsSelectAll;
            onChangeData(newIsSelectAll ? options : []);
            return newIsSelectAll;
          });
        } else {
          const shouldDeselectAll = selectedOption?.length !== options?.length;
          if (shouldDeselectAll && isSelectAll) {
            setIsSelectAll(false);
          }
          onChangeData(selectedOption);
        }
      } else {
        // For Review Page
        const _isSelectAll = selectedOption?.find((item) => item?.value === 'select-all');
        if (selectedOption?.length <= 0) {
          setIsSelectAll(false);
        }

        if (selectedOption?.length !== options?.length) {
          setIsSelectAll(!initialSelectAll);
        }

        if (_isSelectAll) {
          if (!isSelectAll) {
            const missingData = options.filter(
              (item1) =>
                !value?.some((item2) => item2.key === item1.key && item2.value === item1.value) &&
                !(
                  item1?.isDisabled &&
                  countPageDisplayed?.[item1?.label?.toLowerCase()] >= countPageDisplayedMax
                )
            );

            const _options =
              value && value.length > 0
                ? [...value.filter((item) => !item.isDisabled), ...missingData]
                : options.filter(
                    (item) =>
                      !item.isDisabled &&
                      countPageDisplayed &&
                      countPageDisplayed[item?.label?.toLowerCase()] < countPageDisplayedMax
                  );

            setIsSelectAll(true);
            onChangeData(_options);
          } else {
            setIsSelectAll(false);
            onChangeData([]);
          }
        } else {
          onChangeData(selectedOption);
        }
      }
    } else {
      onChangeData(selectedOption);
    }
  };

  return (
    <div className={containerClassName}>
      <label className="font-[500] text-[14px] leading-[17px] text-reliableBlack70">
        {labelName}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {useController ? (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Select
              isMulti={isMulti}
              placeholder={<span className="text-base">{placeholder}</span>}
              options={withSelectAll ? selectOptions : options}
              className={className}
              classNamePrefix="select"
              hideSelectedOptions={!checkedSelect}
              components={
                checkedSelect && {
                  /* eslint-disable-next-line */
                  MultiValue: (props: MultiValueProps<any, boolean, any>) => (
                    <components.MultiValue {...props} />
                  ),
                  /* eslint-disable-next-line */
                  Option: (props: OptionProps<any, boolean, any>) => (
                    /* eslint-disable-next-line */
                    <CheckboxOption {...(props as any)} />
                  ),
                }
              }
              styles={customStyles}
              isDisabled={isLoading || isDisabled}
              onChange={(selectedOption) => {
                let selectedRobotTags = null;
                if (isSeo) {
                  selectedRobotTags = selectedOption;

                  selectedOption.map((item) => {
                    if (item.value === 'index') {
                      const valueNoIndex = selectedOption.filter(
                        (item) => !(item.value === 'noindex')
                      );
                      selectedRobotTags = valueNoIndex;
                    } else if (item.value === 'noindex') {
                      const valueIndex = selectedOption.filter((item) => !(item.value === 'index'));
                      selectedRobotTags = valueIndex;
                    }

                    if (item.value === 'follow') {
                      const valueNoFollow = selectedOption.filter(
                        (item) => !(item.value === 'nofollow')
                      );
                      selectedRobotTags = valueNoFollow;
                    } else if (item.value === 'nofollow') {
                      const valueFollow = selectedOption.filter(
                        (item) => !(item.value === 'follow')
                      );
                      selectedRobotTags = valueFollow;
                    }
                  });

                  field.onChange(selectedRobotTags);
                } else {
                  field.onChange(selectedOption);
                }
                selectedDropDown(isSeo ? selectedRobotTags : selectedOption);
              }}
              value={value ? value : field.value}
              onBlur={field.onBlur}
              menuPlacement={menuPlacement}
              inputValue={inputValue || ''}
              onInputChange={(e) => {
                handleInputChange(e);
              }}
              maxMenuHeight={maxMenuHeight}
              menuIsOpen={menuIsOpen}
              isLoading={isLoading}
              blurInputOnSelect={false}
              closeMenuOnSelect={false}
            />
          )}
        />
      ) : (
        <Select
          isMulti={isMulti}
          placeholder={<span className="text-base">{placeholder}</span>}
          options={withSelectAll ? selectOptions : options}
          className={className}
          classNamePrefix="select"
          hideSelectedOptions={!checkedSelect}
          components={
            checkedSelect
              ? {
                  // MultiValue: (props: MultiValueProps<any, boolean, any>) => (
                  //   <components.MultiValue {...props} />
                  // ),
                  /* eslint-disable-next-line */
                  Option: (props: OptionProps<any, boolean, any>) => (
                    <CheckboxOption
                      isSelectAll={isSelectAll}
                      withSelectAll={withSelectAll}
                      countPageDisplayed={countPageDisplayed}
                      countPageDisplayedMax={countPageDisplayedMax}
                      modalType={modalType}
                      {...props}
                    />
                  ),
                  IndicatorSeparator: () => null,
                  DropdownIndicator,
                }
              : null
          }
          styles={customStyles}
          isDisabled={isLoading || isDisabled}
          onChange={handleSelectChange}
          value={value}
          menuPlacement={menuPlacement}
          inputValue={inputValue || ''}
          onInputChange={(e) => {
            handleInputChange(e);
          }}
          maxMenuHeight={maxMenuHeight}
          menuIsOpen={menuIsOpen}
          onMenuOpen={handleMenuOpen}
          isLoading={isLoading}
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
        />
      )}

      {!showMessageError ? (
        ''
      ) : error ? (
        <div className="flex items-center gap-[5.67px]">
          <span className="text-supportiveRed text-[14px] leading-[20px] font-[400]">{error}</span>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CustomMultiSelect;
